import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Leaf, Mountain, DollarSign, Shield, Award, Satellite, Calendar, Cloud } from 'lucide-react';
import type { GridCell, SatelliteSnapshot } from '../types';
import { format } from 'date-fns';
import { formatWalletAddress } from '../utils/blockchain';
import './GridCellPopup.css';

interface GridCellPopupProps {
  cell: GridCell;
}

// Mock satellite snapshots for demo (would come from API in production)
function getMockSnapshots(cellId: string): SatelliteSnapshot[] {
  const baseDate = new Date();
  const snapshots: SatelliteSnapshot[] = [];

  // Generate 12 monthly snapshots for the past year
  for (let i = 11; i >= 0; i--) {
    const date = new Date(baseDate);
    date.setMonth(date.getMonth() - i);

    snapshots.push({
      id: `${cellId}-snap-${i}`,
      date,
      source: 'Sentinel-2',
      imagePath: `/data/satellite/arariboia-001/${cellId}/${format(date, 'yyyy-MM')}/snapshot.tif`,
      cloudCoverage: Math.random() * 30,
      ndvi: 0.5 + Math.random() * 0.4,
      evi: 0.4 + Math.random() * 0.4,
    });
  }

  return snapshots;
}

export const GridCellPopup: React.FC<GridCellPopupProps> = ({ cell }) => {
  const { metadata, blockchain } = cell;
  const [snapshots, setSnapshots] = useState<SatelliteSnapshot[]>([]);
  const [showSnapshots, setShowSnapshots] = useState(false);

  useEffect(() => {
    // Load satellite snapshots for this cell
    const snapshotData = getMockSnapshots(cell.id);
    setSnapshots(snapshotData);
  }, [cell.id]);

  const chartData = [
    { name: 'BQI', value: metadata.bqiScore },
    { name: 'Habitat', value: metadata.habitatQuality },
    { name: 'Vegetation', value: metadata.vegetationCoverage },
    { name: 'Water', value: metadata.waterQuality },
    { name: 'Soil', value: metadata.soilHealth }
  ];

  return (
    <div className="grid-popup">
      <div className="popup-header">
        <h3 className="popup-title">
          Cell {cell.id}
          {blockchain && (
            <span className="ownership-badge" title="NFT Owned">
              <Shield size={16} style={{ marginLeft: '8px', color: '#c9a961' }} />
            </span>
          )}
        </h3>
        <div className="popup-coordinates">
          {cell.coordinates[0].toFixed(6)}, {cell.coordinates[1].toFixed(6)}
        </div>
      </div>

      {/* Blockchain Ownership Section */}
      {blockchain && (
        <div className="popup-section blockchain-section" style={{
          background: 'linear-gradient(135deg, #1a2642 0%, #2d3e5f 100%)',
          color: 'white',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '12px'
        }}>
          <h4 className="section-title" style={{ color: '#c9a961', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Award size={16} />
            NFT Certificate
          </h4>
          <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ opacity: 0.8 }}>Owner:</span>
              <strong>{blockchain.owner}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ opacity: 0.8 }}>Token ID:</span>
              <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>{blockchain.tokenId}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ opacity: 0.8 }}>Wallet:</span>
              <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>{formatWalletAddress(blockchain.transactionHash)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ opacity: 0.8 }}>Purchased:</span>
              <span>{format(blockchain.timestamp, 'MMM dd, yyyy')}</span>
            </div>
          </div>
        </div>
      )}

      <div className="popup-metrics">
        <div className="metric-card bqi-card">
          <div className="metric-icon">
            <TrendingUp size={20} />
          </div>
          <div className="metric-content">
            <div className="metric-label">BQI Score</div>
            <div className="metric-value">{metadata.bqiScore}</div>
          </div>
        </div>

        <div className="metric-card species-card">
          <div className="metric-icon">
            <Leaf size={20} />
          </div>
          <div className="metric-content">
            <div className="metric-label">Species</div>
            <div className="metric-value">{metadata.speciesCount}</div>
          </div>
        </div>

        <div className="metric-card carbon-card">
          <div className="metric-icon">
            <Mountain size={20} />
          </div>
          <div className="metric-content">
            <div className="metric-label">Carbon (tCO₂)</div>
            <div className="metric-value">{metadata.carbonStock.toFixed(1)}</div>
          </div>
        </div>

        <div className="metric-card credit-card">
          <div className="metric-icon">
            <DollarSign size={20} />
          </div>
          <div className="metric-content">
            <div className="metric-label">Credit Value</div>
            <div className="metric-value">${metadata.creditValue}</div>
          </div>
        </div>
      </div>

      <div className="popup-section">
        <h4 className="section-title">Environmental Indicators</h4>
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="value" fill="#1a2642" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="popup-section">
        <h4 className="section-title">Detailed Metrics</h4>
        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-label">Diversity Index:</span>
            <span className="detail-value">{metadata.diversityIndex.toFixed(2)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">NDVI:</span>
            <span className="detail-value">{metadata.ndvi.toFixed(2)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Water Quality:</span>
            <span className="detail-value">{metadata.waterQuality.toFixed(1)}%</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Soil Health:</span>
            <span className="detail-value">{metadata.soilHealth.toFixed(1)}%</span>
          </div>
        </div>
      </div>

      {/* Satellite Snapshots Section */}
      <div className="popup-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h4 className="section-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Satellite size={14} />
            Satellite Data (Copernicus)
          </h4>
          <button
            onClick={() => setShowSnapshots(!showSnapshots)}
            style={{
              background: 'var(--primary-navy)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '4px 8px',
              fontSize: '11px',
              cursor: 'pointer',
            }}
          >
            {showSnapshots ? 'Hide' : 'Show'} ({snapshots.length})
          </button>
        </div>

        {showSnapshots && snapshots.length > 0 && (
          <div style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '11px' }}>
            {snapshots.slice(0, 12).map((snapshot, index) => (
              <div
                key={snapshot.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '6px 8px',
                  background: index % 2 === 0 ? 'var(--gray-50)' : 'white',
                  borderRadius: '4px',
                  marginBottom: '2px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Calendar size={12} style={{ color: 'var(--gray-500)' }} />
                  <span>{format(new Date(snapshot.date), 'MMM yyyy')}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: 'var(--bio-green)', fontWeight: 500 }}>
                    NDVI: {snapshot.ndvi.toFixed(2)}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '2px', color: 'var(--gray-500)' }}>
                    <Cloud size={10} />
                    {snapshot.cloudCoverage.toFixed(0)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {!showSnapshots && (
          <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>
            {snapshots.length} historical snapshots available (weekly, 1 year)
          </div>
        )}
      </div>

      <div className="popup-footer">
        <span className="footer-text">
          Last updated: {format(metadata.lastUpdated, 'MMM dd, yyyy HH:mm')}
        </span>
      </div>
    </div>
  );
};
